import { useEditor } from '@craftjs/core';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { ReactNode, useCallback, useRef, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { AlignLeft02Icon } from '@/components/icons/AlignLeft02Icon';
import { ChevronRightDoubleIcon } from '@/components/icons/ChevronRightDoubleIcon';
import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { Heading02Icon } from '@/components/icons/Heading02Icon';
import { LayoutAlt04Icon } from '@/components/icons/LayoutAlt04Icon';
import { ListIcon } from '@/components/icons/ListIcon';
import { StickerSquareIcon } from '@/components/icons/StickerSquareIcon';
import { COLORS } from '@/styles/tokens/colors';

import { useIsDraggingExternal } from '../BlockEditor.context';
import { useAutoSelectDroppedNode } from '../hooks/useAutoSelectDroppedNode';
import { EditableCallout } from '../presentation/EditableCallout/EditableCallout';
import { EditableEducationalSlideBody } from '../presentation/EducationalSlideBodyText/EditableEducationalSlideBody';
import { EducationalSlideHeading } from '../presentation/EducationalSlideHeading/EducationalSlideHeading';
import { EditablePresentationListItem } from '../presentation/PresentationListItem/EditablePresentationListItem';

const COLLAPSED_WIDTH = 56;
const EXPANDED_WIDTH = 220;

interface BlockDefinition {
  label: string;
  component: React.ReactElement;
  icon: ReactNode;
}

const BLOCK_DEFINITIONS: BlockDefinition[] = [
  {
    label: 'Heading',
    component: <EducationalSlideHeading title="New heading" variant="h2" />,
    icon: <Heading02Icon size={20} color={COLORS.TEXT.SECONDARY} />,
  },
  {
    label: 'Rich text',
    component: <EditableEducationalSlideBody text="Add text..." />,
    icon: <AlignLeft02Icon size={20} color={COLORS.TEXT.SECONDARY} />,
  },
  {
    label: 'List item',
    component: (
      <EditablePresentationListItem
        title="New list item"
        description="Description text"
        iconType="check"
      />
    ),
    icon: <ListIcon size={20} color={COLORS.TEXT.SECONDARY} />,
  },
  {
    label: 'Callout',
    component: <EditableCallout />,
    icon: <StickerSquareIcon size={20} color={COLORS.TEXT.SECONDARY} />,
  },
];

interface BlockItemProps {
  label: string;
  component: React.ReactElement;
  icon: ReactNode;
  isExpanded: boolean;
}

function BlockItem({ label, component, icon, isExpanded }: BlockItemProps) {
  const { connectors } = useEditor();
  const { setIsDraggingExternal } = useIsDraggingExternal();
  const { captureNodesBeforeDrag, selectNewNodeAfterDrop } =
    useAutoSelectDroppedNode();
  const internalRef = useRef<HTMLDivElement | null>(null);

  const handleDragStart = useCallback(() => {
    captureNodesBeforeDrag();
    setIsDraggingExternal(true);
  }, [captureNodesBeforeDrag, setIsDraggingExternal]);

  const handleDragEnd = useCallback(() => {
    setIsDraggingExternal(false);
    selectNewNodeAfterDrop();
  }, [selectNewNodeAfterDrop, setIsDraggingExternal]);

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Cleanup previous listeners
      if (internalRef.current) {
        internalRef.current.removeEventListener('dragstart', handleDragStart);
        internalRef.current.removeEventListener('dragend', handleDragEnd);
      }

      internalRef.current = node;

      if (node) {
        connectors.create(node, component);
        node.addEventListener('dragstart', handleDragStart);
        node.addEventListener('dragend', handleDragEnd);
      }
    },
    [connectors, component, handleDragStart, handleDragEnd]
  );

  return (
    <Box
      ref={setRef}
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: isExpanded ? 'flex-start' : 'center',
        gap: 1.5,
        p: isExpanded ? 1 : 0.5,
        pl: isExpanded ? 1.5 : 0.5,
        minHeight: (theme) => (isExpanded ? theme.spacing(5) : undefined),
        borderRadius: isExpanded ? 0.5 : 1,
        border: isExpanded ? `1px solid ${COLORS.NAVY[100]}` : 'none',
        backgroundColor: COLORS.PRIMITIVES.WHITE,
        cursor: 'grab',
        '&:hover': {
          borderColor: isExpanded ? COLORS.NAVY[100] : COLORS.GRAY[300],
          backgroundColor: isExpanded ? COLORS.NAVY[50] : COLORS.GRAY[50],
        },
        '&:active': {
          ...(isExpanded && { backgroundColor: COLORS.NAVY[50] }),
          cursor: 'grabbing',
        },
      }}
    >
      {!isExpanded && <Stack p={1}>{icon}</Stack>}
      {isExpanded && (
        <Typography
          variant="body-xs"
          whiteSpace="nowrap"
          sx={{ color: COLORS.TEXT.PRIMARY, display: 'flex', flexGrow: 1 }}
        >
          {label}
        </Typography>
      )}
      {isExpanded && <Copy07Icon size={16} color={COLORS.TEXT.PRIMARY} />}
    </Box>
  );
}

interface BlockEditorSidebarProps {
  defaultExpanded?: boolean;
}

export function BlockEditorSidebar({
  defaultExpanded = true,
}: BlockEditorSidebarProps) {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggle = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <Box
      sx={{
        width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        flexShrink: 0,
        backgroundColor: COLORS.PRIMITIVES.WHITE,
        borderRight: `1px solid ${COLORS.GRAY[200]}`,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: `width ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeOut}`,
        overflow: 'hidden',
      }}
    >
      {/* Header with icon */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={isExpanded ? 'flex-start' : 'center'}
        gap={isExpanded ? 1.5 : 0}
        sx={{
          px: 2,
          py: 2,
          minHeight: 56,
        }}
      >
        <LayoutAlt04Icon size={20} color={COLORS.TEXT.SECONDARY} />
        {isExpanded && (
          <Typography variant="title-xs" whiteSpace="nowrap">
            Add components
          </Typography>
        )}
      </Stack>

      {/* Block list */}
      <Stack
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: isExpanded ? 2 : 0,
          gap: 0.5,
          alignItems: isExpanded ? 'flex-start' : 'center',
        }}
      >
        {BLOCK_DEFINITIONS.map((block) => (
          <BlockItem
            key={block.label}
            label={block.label}
            component={block.component}
            icon={block.icon}
            isExpanded={isExpanded}
          />
        ))}
      </Stack>

      {/* Collapse toggle */}
      <Box
        sx={{
          p: 1,
        }}
      >
        <Button
          variant="transparent"
          size="sm"
          onClick={toggle}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            p: 1,
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <ChevronRightDoubleIcon
              size={20}
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: `transform ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeOut}`,
              }}
            />
            {isExpanded && <Typography variant="body-xs">Collapse</Typography>}
          </Stack>
        </Button>
      </Box>
    </Box>
  );
}
